import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import client from "../utils/contentfulClient";
import { Box, Typography, CircularProgress } from "@mui/material";
import ListWithIcons from "../components/ListWithIcons";
import IconsContainer from "../components/IconsContainer";
import QuickCards from "../components/QuickCards";
import Gallery from "../components/ImageGallery";
import CountersGroup from "../components/CountersGroup";
import DividerConTitulo from "../components/DividerConTitulo";
import NotFound from "../pages/NotFound";
import RichTextBlock from "../components/RichTextBlock"; // NUEVO: Importa el componente
import ImageCarouselBlock from "../components/ImageCarouselBlock";
import GoogleSlidesEmbed from "../components/GoogleSlidesEmbed";
import ConvocatoriaCardsGrid from "../components/ConvocatoriaCardsGrid";
import ListadoPDF from "../components/ListadoPDF";

const DynamicPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Debug: ver los componentes recibidos desde Contentful
  React.useEffect(() => {
    if (pageData && pageData.componentes) {
      // no-op
    }
  }, [pageData]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await client.getEntries({
          content_type: "paginaDinamica",
          "fields.slug": slug,
          limit: 1,
          include: 2,
        });

        if (res.items.length > 0) {
          setPageData(res.items[0].fields);
        } else {
          setPageData(null);
        }
      } catch (error) {
        setPageData(null); // no-op
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
        <p>Obteniendo contenido...</p>
      </Box>
    );
  }

  if (!pageData) {
    // Debug: ver los componentes recibidos desde Contentful
    if (pageData && pageData.componentes) {
      // no-op
    }
    return <NotFound />;
  }

  return (
    <Box
      sx={{
        mx: "auto",
        mt: { xs: "8px", sm: "16px", md: "34px" },
        mb: { xs: "8px", sm: "16px", md: "34px" },
      }}
    >
      {pageData.banner && (
        <img
          src={`https:${pageData.banner.fields.file.url}`}
          alt={pageData.titulo || "Banner"}
          style={{
            width: "100%",
            maxHeight: "200px",
            objectFit: "cover",
            marginBottom: "24px",
          }}
        />
      )}
      <Typography variant="h4" gutterBottom sx={{ px: { xs: 2, md: 3 } }}>
        {pageData.titulo}
      </Typography>

      {/* Renderiza RichText con soporte de assets */}
      {pageData.descripcion && (
        <RichTextBlock content={pageData.descripcion} />
      )}

      {/* Renderizar componentes dinámicamente si existen */}
      {pageData.componentes?.filter(Boolean).map((componente, index) => {
        // defensiva: componente puede ser null/undefined or unresolved Link
        if (!componente) return null;
        const fields = componente.fields || {};
        const tipo = componente.sys?.contentType?.sys?.id || componente.sys?.contentType || null;

        // si no tenemos tipo, ignoramos este componente (posible referencia no resuelta)
        if (!tipo) return null;

        // Adaptar mapeo para bloqueInteractivo igual que en Home/fetchPaginaPrincipal
        let mappedFields = fields;
        if (tipo === "bloqueInteractivo") {
          mappedFields = {
            ...fields,
            elementos: (fields.elementos || []).map((elemento) => ({
              titulo: elemento.fields?.titulo || "Sin título",
              contenido: elemento.fields?.contenido || null,
              background: elemento.fields?.background?.fields?.file?.url
                ? `https:${elemento.fields.background.fields.file.url}`
                : null,
              icono: elemento.fields?.image?.fields?.file?.url
                ? `https:${elemento.fields.image.fields.file.url}`
                : null,
              componente: elemento.fields?.componente
                ? {
                    tipo: elemento.fields.componente.sys?.contentType?.sys?.id || null,
                    ...(elemento.fields.componente.fields || {}),
                  }
                : null,
            })),
          };
        }

        return (
          <Box key={index} sx={{ marginBottom: "32px" }}>
            {tipo === "bloqueInteractivo" ? (
              <ListWithIcons services={mappedFields} />
            ) : tipo === "IconsContainer" ? (
              <IconsContainer iconos={fields.iconos} />
            ) : tipo === "tarjetasRapidas" ? (
              <QuickCards
                cardsData={
                    (fields.tarjetas || []).map((card) => {
                      const cf = card?.fields || {};
                      const iconUrl = cf.icono?.fields?.file?.url
                        ? `https:${cf.icono.fields.file.url}`
                        : null;
                      const pdfUrl = cf.pdf?.fields?.file?.url
                        ? `https:${cf.pdf.fields.file.url}`
                        : null;
                      return {
                        icon: iconUrl ? <img src={iconUrl} alt="icono" width={32} /> : null,
                        pdf: pdfUrl,
                        title: cf.titulo || cf.title || "",
                        description: cf.descripcion || cf.description || "",
                        extraInfo: cf.informacionExtra || "",
                        route: cf.ruta || "",
                      };
                    })
                }
              />
            ) : tipo === "galeriaDeImagenes" ? (
              <Box
                sx={{
                  textAlign: "center",
                  px: 2,
                  maxHeight: "1200px",
                }}
              >
                {fields.imagenes && Array.isArray(fields.imagenes) && fields.imagenes.length > 0 ? (
                  <Gallery
                    images={fields.imagenes}
                    sx={{
                      borderRadius: "20px",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay imágenes para mostrar en esta galería.
                  </Typography>
                )}
              </Box>
            ) : tipo === "contadorAcuicola" ? (
              <CountersGroup
                title={fields.titulo}
                total={fields.cantidadTotal}
                municipios={fields.municipios}
                pdf={fields.pdf}
                coordenadas={fields.coordenadas?.locations}
              />
            ) : tipo === "dividerConTitulo" ? (
              <DividerConTitulo titulo={fields.titulo} />
            ) : tipo === "richTextBlock" ? ( // NUEVO: Renderiza RichTextBlock
              <RichTextBlock content={fields.texto} title={fields.titulo} />
            ) : tipo === "imageCarouselBlock" ? (
              <ImageCarouselBlock
                images={fields.imagenes}
                description={fields.description}
                background={fields.colorDeFondo}
              />
            ) : tipo === "convocatoriaCardsGrid" ? (
              <ConvocatoriaCardsGrid />
            ) : tipo === "listadoPDF" ? (
              <ListadoPDF
                titulo={fields.titulo}
                documentos={fields.documentos || fields.pdfs || fields.hijos || []}
              />
            ) : tipo === "presentacionSlide" ? (
              <GoogleSlidesEmbed embedCode={fields.codigoSlide} titulo={fields.titulo} mostrarTitulo={fields.mostrarTitulo} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Componente no soportado: {tipo}
              </Typography>
            )}
          </Box>
        );
      })}
      
    </Box>
  );
};

export default DynamicPage;
